import {
  Box,
  CircularProgress,
  Collapse,
  Grow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Icon } from '../../icon/icon';
import { theme } from '../../../styles/theme';
import { updateMeta } from '../../../redux/meta';
import { useAppDispatch } from '../../../pages/assessment';

type PropTypes = {
  text: string;
  duration?: number;
  showIcon?: boolean;
};

const ANIMATION_DURATION = 1200;
const TOTAL_SCREEN_DURATION = 6000;

const LoadingIcon = ({ text, showIcon, duration = 500 }: PropTypes) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let timeout;
    if (showIcon) {
      timeout = setTimeout(() => {
        setIsLoading(false);
      }, duration);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [showIcon, duration]);

  return (
    <Box sx={{ my: 3, display: showIcon ? 'flex' : 'none' }}>
      <Box sx={{ mr: 3 }}>
        <Collapse in={isLoading} sx={{ width: isLoading ? 'inherit' : 0 }}>
          <CircularProgress color="primary" size={24} />
        </Collapse>
        <Grow in={!isLoading} timeout={500}>
          <Box>
            <Icon
              icon="Check"
              styleType="regular"
              htmlColor={theme.palette.sky.dark}
            />
          </Box>
        </Grow>
      </Box>
      <Typography variant="body1">{text}</Typography>
    </Box>
  );
};

export const MatchLoading = () => {
  const dispatch = useAppDispatch();

  const loadingSteps = [
    { text: 'Location' },
    { text: 'Condition' },
    { text: 'Age' },
  ];

  // -1 gives us a delay before the first animation
  const [currentStep, setCurrentStep] = useState(-1);

  useEffect(() => {
    const animateNextStep = () => {
      if (currentStep < loadingSteps.length - 1) {
        setTimeout(() => {
          setCurrentStep(currentStep + 1);
        }, ANIMATION_DURATION);
      }
    };

    if (currentStep < loadingSteps.length) {
      animateNextStep();
    }
  }, [currentStep, loadingSteps]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(updateMeta());
    }, TOTAL_SCREEN_DURATION);

    return () => {
      clearTimeout(timeout);
    };
  }, [dispatch]);

  return (
    <>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Searching matches based on
      </Typography>
      {loadingSteps.map((step, index) => (
        <LoadingIcon
          key={index}
          text={step.text}
          duration={ANIMATION_DURATION}
          showIcon={index <= currentStep}
        />
      ))}
    </>
  );
};
